import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, quranAPI, getAuthToken } from '../utils/api';
import { ChevronLeft, Play, Pause, Search } from 'lucide-react';
import { toast } from 'sonner';

const ListenQuran = () => {
  const { surahNumber } = useParams();
  if (surahNumber) return <SurahPlayer surahNumber={parseInt(surahNumber, 10)} />;
  return <ListenSurahList />;
};

const ListenSurahList = () => {
  const { isKidsMode } = useMode();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [surahs, setSurahs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const meReq = getAuthToken() ? authAPI.getMe().catch(() => ({ data: null })) : Promise.resolve({ data: null });
        const [u, s] = await Promise.all([meReq, quranAPI.getSurahList()]);
        setUser(u.data);
        setSurahs(s.data?.data || []);
      } catch {
        toast.error(lang === 'tr' ? 'Yüklenemedi' : 'Failed');
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  const filtered = surahs.filter((s) =>
    !search.trim() || s.name_en.toLowerCase().includes(search.toLowerCase()) || String(s.number).includes(search)
  );

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Kuran Dinle' : 'Listen to Quran'}>
      <div className="max-w-6xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{lang === 'tr' ? 'Kuran' : 'Quran'}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Kuran Dinle' : 'Listen to Quran'}
          </h1>
          <p className="text-stone-600 mt-2">
            {lang === 'tr' ? 'Sevdiğin karinin sesinden Kuran dinle.' : 'Listen with your favorite reciter.'}
          </p>
        </header>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === 'tr' ? 'Sure ara...' : 'Search surah...'}
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-stone-500">{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((s) => (
              <button
                key={s.number}
                type="button"
                onClick={() => navigate(`/dinle/${s.number}`)}
                data-testid={`listen-surah-${s.number}`}
                className="text-left p-4 bg-white rounded-2xl border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all flex items-center gap-3"
              >
                <div className={`w-12 h-12 rounded-xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'} flex items-center justify-center font-bold text-sm`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {s.number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-stone-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.name_en}</div>
                  <div className="text-xs text-stone-500">{s.ayah_count} {lang === 'tr' ? 'ayet' : 'ayahs'}</div>
                </div>
                <Play className={`w-5 h-5 ${isKidsMode ? 'text-amber-600' : 'text-teal-600'}`} />
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};

const SurahPlayer = ({ surahNumber }) => {
  const { isKidsMode } = useMode();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [arabic, setArabic] = useState(null);
  const [reciters, setReciters] = useState([]);
  const [reciter, setReciter] = useState('alafasy');
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0); // 0..1
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const accent = isKidsMode ? 'amber' : 'teal';

  useEffect(() => {
    (async () => {
      try {
        const meReq = getAuthToken() ? authAPI.getMe().catch(() => ({ data: null })) : Promise.resolve({ data: null });
        const [u, r, ar] = await Promise.all([
          meReq,
          quranAPI.getReciters(),
          quranAPI.getSurah(surahNumber, 'quran-uthmani'),
        ]);
        setUser(u.data);
        setReciters(r.data?.data || []);
        setArabic(ar.data);
      } catch {
        toast.error(lang === 'tr' ? 'Yüklenemedi' : 'Failed');
      } finally {
        setLoading(false);
      }
    })();
  }, [surahNumber, lang]);

  // Switch reciter -> reload audio but preserve playing intent
  useEffect(() => {
    if (audioRef.current) {
      const wasPlaying = !audioRef.current.paused;
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
      setPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      setDuration(0);
      if (wasPlaying) {
        // Resume on the new reciter
        setTimeout(() => { playSurah(); }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reciter]);

  const playSurah = async () => {
    if (audioRef.current) {
      try { await audioRef.current.play(); setPlaying(true); return; } catch {}
    }
    const src = quranAPI.getSurahAudioUrl(reciter, surahNumber);
    const a = new Audio(src);
    a.preload = 'auto';
    audioRef.current = a;
    a.ontimeupdate = () => {
      setCurrentTime(a.currentTime || 0);
      if (a.duration && Number.isFinite(a.duration)) {
        setProgress(a.currentTime / a.duration);
      }
    };
    a.onloadedmetadata = () => {
      if (Number.isFinite(a.duration)) setDuration(a.duration);
    };
    a.onended = () => { setPlaying(false); setProgress(1); };
    a.onerror = () => {
      setPlaying(false);
      toast.error(lang === 'tr' ? 'Ses oynatılamadı' : 'Playback failed');
    };
    try {
      await a.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  };

  const togglePlay = async () => {
    if (audioRef.current && playing) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    playSurah();
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  useEffect(() => () => {
    if (audioRef.current) try { audioRef.current.pause(); } catch {}
  }, []);

  if (loading || !arabic) {
    return (
      <AppShell user={user}>
        <div className="max-w-3xl mx-auto p-8 text-center text-stone-500">{t('common.loading')}</div>
      </AppShell>
    );
  }

  return (
    <AppShell user={user} title={arabic.name_en}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <button type="button" onClick={() => navigate('/dinle')} className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
          <ChevronLeft className="w-4 h-4" /> {lang === 'tr' ? 'Sure Listesi' : 'Surah List'}
        </button>

        <header className="text-center py-6 border-b border-stone-200">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">Sure {arabic.number}</p>
          <h1 className={`text-3xl sm:text-4xl font-bold ${isKidsMode ? 'text-amber-700' : 'text-teal-800'}`} style={{ fontFamily: 'Amiri, serif', direction: 'rtl' }}>
            {arabic.name_ar}
          </h1>
          <p className="text-stone-600 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{arabic.name_en}</p>
          <p className="text-xs text-stone-500 mt-2">{arabic.ayahs.length} {lang === 'tr' ? 'ayet' : 'ayahs'} • {lang === 'tr' ? 'Kesintisiz dinleme' : 'Continuous playback'}</p>
        </header>

        {/* Reciter selector */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {reciters.map((r) => (
            <button
              key={r.slug}
              type="button"
              onClick={() => setReciter(r.slug)}
              data-testid={`reciter-${r.slug}`}
              className={`p-3 rounded-xl text-sm font-medium border transition-all ${
                reciter === r.slug
                  ? isKidsMode ? 'bg-amber-500 text-white border-amber-600' : 'bg-teal-700 text-white border-teal-800'
                  : 'bg-white text-stone-700 border-stone-200 hover:border-stone-300'
              }`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {lang === 'en' ? r.name_en : r.name}
            </button>
          ))}
        </div>

        {/* Player controls */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
          {/* Progress bar */}
          <div
            onClick={seek}
            className="w-full h-2 bg-stone-200 rounded-full cursor-pointer overflow-hidden"
            data-testid="player-progress"
          >
            <div
              className={`h-full ${isKidsMode ? 'bg-amber-500' : 'bg-teal-700'} transition-all`}
              style={{ width: `${(progress * 100).toFixed(2)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-stone-500 font-mono">
            <span>{fmt(currentTime)}</span>
            <span>{fmt(duration)}</span>
          </div>
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={togglePlay}
              data-testid="player-toggle"
              className={`p-5 rounded-2xl ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'} text-white shadow-md transition-colors`}
            >
              {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Ayah list - read-only display, scrolls with audio if user wants */}
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {arabic.ayahs.map((ayah) => (
            <div
              key={ayah.number}
              data-testid={`listen-ayah-${ayah.number}`}
              className="w-full text-right p-4 rounded-xl border bg-white border-stone-200"
              style={{ fontFamily: 'Amiri, serif', direction: 'rtl' }}
            >
              <span className={`inline-block ml-3 px-2 py-0.5 rounded-full text-xs font-bold ${isKidsMode ? 'bg-amber-200 text-amber-900' : 'bg-teal-100 text-teal-900'}`}>
                {ayah.number}
              </span>
              <span className="text-xl leading-loose">{ayah.text}</span>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
};

export default ListenQuran;
